import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './OfferDistribution.css';

interface OfferTemplate {
  templateId: string;
  name: string;
  titleTemplate: string;
  contentTemplate: string;
  defaultHashtags: string[];
  category: string;
  requiresDiscount: boolean;
  requiresMenuItem: boolean;
  mediaTemplateUrls: string[];
}

interface Discount {
  discountId: string;
  name: string;
  discountType: string;
  value: number;
  isActive: boolean;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  validFrom?: string;
  validUntil?: string;
}

interface MenuItem {
  menuItemId: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  category: string;
}

interface SocialMediaAccount {
  accountId: string;
  platform: string;
  accountName: string;
  isActive: boolean;
  trackingPixelId?: string;
  conversionPixelId?: string;
}

interface Campaign {
  campaignId: string;
  name: string;
  status: string;
  startDate: string;
  endDate?: string;
}

interface OfferDistributionRequest {
  title: string;
  content: string;
  hashtags: string[];
  mentions: string[];
  platforms: string[];
  selectedDiscount?: string;
  selectedMenuItem?: string;
  selectedCampaign?: string;
  distributionType: 'immediate' | 'scheduled' | 'bulk_schedule';
  scheduledDates: string[];
  timezone: string;
  
  // Code generation settings
  generateCodes: boolean;
  codePrefix: string;
  codesPerPlatform: number;
  codeType: 'single_use' | 'multi_use' | 'unlimited';
  maxUsesPerCode: number;
  codeValidityDays: number;
  
  // Boosting settings
  enableBoosting: boolean;
  boostBudgetPerPost: number;
  boostDuration: number;
  
  // Tracking settings
  enableTracking: boolean;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  
  // Restaurant specific
  restaurantSpecific: boolean;
  minPurchaseAmount: number;
  maxDiscountAmount: number;
}

const PLATFORM_ICONS = {
  facebook: 'FB',
  instagram: 'IG', 
  twitter: 'TW',
  linkedin: 'LI',
  tiktok: 'TT'
};

interface OfferDistributionProps {
  storeId?: string;
}

export const OfferDistribution: React.FC<OfferDistributionProps> = ({ storeId: propStoreId }) => {
  const { getIdToken } = useAuth();
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [accounts, setAccounts] = useState<SocialMediaAccount[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>(propStoreId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [offer, setOffer] = useState<OfferDistributionRequest>({
    title: '',
    content: '',
    hashtags: [],
    mentions: [],
    platforms: [],
    selectedDiscount: '',
    selectedMenuItem: '',
    selectedCampaign: '',
    distributionType: 'immediate',
    scheduledDates: [],
    timezone: 'America/New_York',
    
    generateCodes: true,
    codePrefix: 'SOCIAL',
    codesPerPlatform: 50,
    codeType: 'single_use',
    maxUsesPerCode: 1,
    codeValidityDays: 30,
    
    enableBoosting: false,
    boostBudgetPerPost: 25,
    boostDuration: 7,
    
    enableTracking: true,
    utmSource: 'social-media',
    utmMedium: 'social',
    utmCampaign: 'offer-distribution',
    
    restaurantSpecific: true,
    minPurchaseAmount: 0,
    maxDiscountAmount: 0
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (propStoreId) {
      setSelectedStore(propStoreId);
    }
  }, [propStoreId]);

  useEffect(() => {
    if (selectedStore) {
      fetchStoreData();
    }
  }, [selectedStore]);

  const fetchInitialData = async () => {
    try {
      const token = await getIdToken();

      // Fetch stores (skip if storeId provided via prop)
      if (!propStoreId) {
        const storesResponse = await fetch(API_ENDPOINTS.stores(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (storesResponse.ok) {
          const storesData = await storesResponse.json();
          setStores(storesData.data || []);
          if (storesData.data?.length > 0) {
            setSelectedStore(storesData.data[0].storeId);
            setOffer(prev => ({
              ...prev,
              timezone: storesData.data[0].timezone || 'America/New_York'
            }));
          }
        }
      }

      // Fetch templates
      const templatesResponse = await fetch(API_ENDPOINTS.socialMediaTemplates(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (templatesResponse.ok) {
        const templatesData = await templatesResponse.json();
        setTemplates(templatesData.data || []);
      }

      // Fetch campaigns
      const campaignsResponse = await fetch(API_ENDPOINTS.socialMediaCampaigns(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (campaignsResponse.ok) {
        const campaignsData = await campaignsResponse.json();
        setCampaigns(campaignsData.data || []);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch initial data');
    }
  };

  const fetchStoreData = async () => {
    if (!selectedStore) return;
    
    try {
      const token = await getIdToken();
      
      // Fetch social media accounts for this store
      const accountsResponse = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}?storeId=${selectedStore}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        setAccounts(accountsData.data?.filter((acc: SocialMediaAccount) => acc.isActive) || []);
      }

      // Fetch discounts
      try {
        const discountsResponse = await fetch(`${API_ENDPOINTS.discountCodes()}?storeId=${selectedStore}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (discountsResponse.ok) {
          const discountsData = await discountsResponse.json();
          setDiscounts(discountsData.data || []);
        } else {
          // Mock discount data
          setDiscounts([
            {
              discountId: 'disc_001',
              name: '15% Off Appetizers',
              discountType: 'percentage',
              value: 15,
              isActive: true,
              minPurchaseAmount: 20
            },
            {
              discountId: 'disc_002', 
              name: '$5 Off Entrees',
              discountType: 'fixed_amount',
              value: 5,
              isActive: true,
              minPurchaseAmount: 25
            }
          ]);
        }
      } catch (err) {
        console.log('Using mock discount data');
      }

      // Fetch menu items for this store
      const menuResponse = await fetch(`${API_ENDPOINTS.menuItems()}?storeId=${selectedStore}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (menuResponse.ok) {
        const menuData = await menuResponse.json();
        setMenuItems(menuData.data?.slice(0, 20) || []); // Limit to 20 items for UI performance
      }

    } catch (err) {
      console.error('Failed to fetch store data:', err);
    }
  };

  const handleTemplateApply = (templateId: string) => {
    const template = templates.find(t => t.templateId === templateId);
    if (!template) return;

    // Replace template placeholders with dynamic content
    let title = template.titleTemplate;
    let content = template.contentTemplate;
    
    if (offer.selectedMenuItem && menuItems.length > 0) {
      const menuItem = menuItems.find(m => m.menuItemId === offer.selectedMenuItem);
      if (menuItem) {
        title = title.replace('{MENU_ITEM}', menuItem.name);
        content = content.replace('{MENU_ITEM}', menuItem.name);
        content = content.replace('{PRICE}', `$${menuItem.price.toFixed(2)}`);
      }
    }
    
    if (offer.selectedDiscount && discounts.length > 0) {
      const discount = discounts.find(d => d.discountId === offer.selectedDiscount);
      if (discount) {
        const discountText = discount.discountType === 'percentage' 
          ? `${discount.value}% off` 
          : `$${discount.value} off`;
        content = content.replace('{DISCOUNT}', discountText);
      }
    }
    
    const storeName = stores.find(s => s.storeId === selectedStore)?.name || 'Restaurant';
    title = title.replace('{RESTAURANT_NAME}', storeName);
    content = content.replace('{RESTAURANT_NAME}', storeName);

    setOffer({
      ...offer,
      title,
      content,
      hashtags: template.defaultHashtags
    });
  };

  const handleDistributeOffer = async () => {
    if (!selectedStore || !offer.content || offer.platforms.length === 0) {
      setError('Please fill in all required fields and select at least one platform');
      return;
    }

    if (offer.generateCodes && !offer.selectedDiscount) {
      setError('Please select a discount to generate codes');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getIdToken();
      
      // Step 1: Create campaign if needed
      let campaignId = offer.selectedCampaign;
      if (!campaignId && offer.title) {
        const campaignResponse = await fetch(API_ENDPOINTS.socialMediaCampaigns(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            storeId: selectedStore,
            name: `${offer.title} - Auto Campaign`,
            description: `Auto-generated campaign for offer distribution`,
            campaignType: 'promotion',
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + offer.codeValidityDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            status: 'active'
          })
        });
        
        if (campaignResponse.ok) {
          const campaignData = await campaignResponse.json();
          campaignId = campaignData.data.campaignId;
        }
      }

      // Step 2: Generate discount codes if needed
      const generatedCodes: { [platform: string]: string[] } = {};
      
      if (offer.generateCodes && offer.selectedDiscount) {
        for (const platform of offer.platforms) {
          const codeResponse = await fetch(API_ENDPOINTS.generateDiscountCodes(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              storeId: selectedStore,
              discountId: offer.selectedDiscount,
              quantity: offer.codesPerPlatform,
              codePrefix: `${offer.codePrefix}${platform.toUpperCase()}`,
              codeLength: 8,
              codeType: offer.codeType,
              maxUses: offer.maxUsesPerCode,
              validFrom: new Date().toISOString(),
              validUntil: new Date(Date.now() + offer.codeValidityDays * 24 * 60 * 60 * 1000).toISOString(),
              campaignId,
              distributedVia: platform,
              restaurantSpecific: offer.restaurantSpecific,
              minPurchaseAmount: offer.minPurchaseAmount,
              maxDiscountAmount: offer.maxDiscountAmount
            })
          });
          
          if (codeResponse.ok) {
            const codesData = await codeResponse.json();
            generatedCodes[platform] = codesData.data.codes || [];
          }
        }
      }

      // Step 3: Create and distribute social media posts
      const postPromises = offer.platforms.map(async (platform) => {
        const platformCodes = generatedCodes[platform] || [];
        const sampleCode = platformCodes[0] || '';
        
        // Inject discount code into content
        let postContent = offer.content;
        if (sampleCode) {
          postContent += `\n\nUse code: ${sampleCode}`;
          if (offer.minPurchaseAmount > 0) {
            postContent += ` (min. $${offer.minPurchaseAmount})`;
          }
          postContent += ` - Limited time offer!`;
        }
        
        // Add UTM parameters for tracking
        let trackingUrl = '';
        if (offer.enableTracking) {
          const baseUrl = `https://bigsystemone.com/menu/${selectedStore}`;
          const utmParams = new URLSearchParams({
            utm_source: offer.utmSource,
            utm_medium: offer.utmMedium,
            utm_campaign: offer.utmCampaign,
            utm_content: platform,
            ...(sampleCode && { promo_code: sampleCode })
          });
          trackingUrl = `${baseUrl}?${utmParams.toString()}`;
          postContent += `\n\nOrder now: ${trackingUrl}`;
        }
        
        // Add hashtags
        if (offer.hashtags.length > 0) {
          postContent += `\n\n${offer.hashtags.map(tag => `#${tag}`).join(' ')}`;
        }

        const postData = {
          storeId: selectedStore,
          title: offer.title,
          content: postContent,
          hashtags: offer.hashtags,
          mentions: offer.mentions,
          platforms: [platform],
          campaignId,
          discountId: offer.selectedDiscount,
          offerCode: sampleCode,
          menuItemId: offer.selectedMenuItem,
          
          // Scheduling
          scheduledAt: offer.distributionType === 'immediate' 
            ? new Date().toISOString() 
            : offer.scheduledDates[0] || new Date().toISOString(),
          timezone: offer.timezone,
          publishImmediately: offer.distributionType === 'immediate',
          
          // Boosting
          isBoosted: offer.enableBoosting,
          boostBudget: offer.boostBudgetPerPost,
          boostDuration: offer.boostDuration,
          
          // Tracking
          utmParameters: offer.enableTracking ? {
            source: offer.utmSource,
            medium: offer.utmMedium,
            campaign: offer.utmCampaign,
            content: platform
          } : {},
          
          trackingPixels: {} as { [key: string]: string }
        };

        // Add platform-specific tracking pixels
        const account = accounts.find(acc => acc.platform === platform);
        if (account) {
          if (account.trackingPixelId) {
            postData.trackingPixels[platform] = account.trackingPixelId;
          }
          if (account.conversionPixelId) {
            postData.trackingPixels[`${platform}_conversion`] = account.conversionPixelId;
          }
        }

        return fetch(API_ENDPOINTS.socialMediaPosts(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(postData)
        });
      });

      // Execute all post creations
      const postResponses = await Promise.all(postPromises);
      const successCount = postResponses.filter(r => r.ok).length;
      const failCount = postResponses.length - successCount;

      let successMessage = `Successfully distributed offer to ${successCount} platform${successCount !== 1 ? 's' : ''}`;
      
      if (offer.generateCodes) {
        const totalCodes = Object.values(generatedCodes).reduce((sum, codes) => sum + codes.length, 0);
        successMessage += ` and generated ${totalCodes} unique discount codes`;
      }
      
      if (failCount > 0) {
        successMessage += `. ${failCount} platform${failCount !== 1 ? 's' : ''} failed - please check your account connections.`;
      }

      setSuccess(successMessage);
      
      // Reset form
      setOffer({
        ...offer,
        title: '',
        content: '',
        hashtags: [],
        mentions: [],
        selectedMenuItem: '',
        selectedDiscount: '',
        selectedCampaign: ''
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to distribute offer');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (!offer.content) return null;
    
    const discount = discounts.find(d => d.discountId === offer.selectedDiscount);
    const menuItem = menuItems.find(m => m.menuItemId === offer.selectedMenuItem);
    const sampleCode = `${offer.codePrefix}FB001`;
    
    let previewContent = offer.content;
    if (offer.generateCodes && discount) {
      previewContent += `\n\nUse code: ${sampleCode}`;
      if (offer.minPurchaseAmount > 0) {
        previewContent += ` (min. $${offer.minPurchaseAmount})`;
      }
      previewContent += ` - Limited time offer!`;
    }
    
    if (offer.enableTracking) {
      previewContent += `\n\nOrder now: https://bigsystemone.com/menu/${selectedStore}?utm_source=${offer.utmSource}&promo_code=${sampleCode}`;
    }
    
    if (offer.hashtags.length > 0) {
      previewContent += `\n\n${offer.hashtags.map(tag => `#${tag}`).join(' ')}`;
    }

    return (
      <div className="offer-preview">
        <h3>Post Preview</h3>
        <div className="preview-content">
          {offer.title && <div className="preview-title">{offer.title}</div>}
          <div className="preview-text">{previewContent}</div>
          {menuItem && menuItem.imageUrl && (
            <div className="preview-media">
              <img src={menuItem.imageUrl} alt={menuItem.name} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="offer-distribution">
      <div className="distribution-header">
        <h1>Offer Distribution</h1>
        <p>Create and distribute promotional offers across multiple social media platforms with automatic discount code generation.</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>ERROR: {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="success-banner">
          <span>SUCCESS: {success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="distribution-form">
        {!propStoreId && (
          <div className="form-section">
            <h2>Store Selection</h2>
            <div className="form-group">
              <label>Restaurant Location:</label>
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
              >
                {stores.map(store => (
                  <option key={store.storeId} value={store.storeId}>
                    {store.name} - {store.address?.city}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {accounts.length === 0 && selectedStore && (
          <div className="warning-banner">
            <span>WARNING: No active social media accounts found for this location. Please connect your social media accounts first.</span>
          </div>
        )}

        {templates.length > 0 && (
          <div className="form-section">
            <h2>Quick Templates</h2>
            <div className="template-grid">
              {templates.slice(0, 6).map(template => (
                <button
                  key={template.templateId}
                  className="template-card"
                  onClick={() => handleTemplateApply(template.templateId)}
                >
                  <div className="template-name">{template.name}</div>
                  <div className="template-category">{template.category}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>Offer Content</h2>
          
          <div className="form-group">
            <label>Title (optional):</label>
            <input
              type="text"
              value={offer.title}
              onChange={(e) => setOffer({...offer, title: e.target.value})}
              placeholder="Limited Time Special!"
            />
          </div>

          <div className="form-group">
            <label>Content *:</label>
            <textarea
              value={offer.content}
              onChange={(e) => setOffer({...offer, content: e.target.value})}
              placeholder="Tell your customers about this amazing offer..."
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Hashtags:</label>
            <input
              type="text"
              value={offer.hashtags.join(', ')}
              onChange={(e) => setOffer({
                ...offer, 
                hashtags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
              })}
              placeholder="restaurant, food, special, limited-time"
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Offer Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Featured Menu Item (optional):</label>
              <select
                value={offer.selectedMenuItem}
                onChange={(e) => setOffer({...offer, selectedMenuItem: e.target.value})}
              >
                <option value="">No specific item</option>
                {menuItems.map(item => (
                  <option key={item.menuItemId} value={item.menuItemId}>
                    {item.name} - ${item.price.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Discount *:</label>
              <select
                value={offer.selectedDiscount}
                onChange={(e) => setOffer({...offer, selectedDiscount: e.target.value})}
                required
              >
                <option value="">Select a discount</option>
                {discounts.filter(d => d.isActive).map(discount => (
                  <option key={discount.discountId} value={discount.discountId}>
                    {discount.name} - 
                    {discount.discountType === 'percentage' ? ` ${discount.value}% off` : 
                     discount.discountType === 'fixed_amount' ? ` $${discount.value} off` :
                     ' BOGO'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {campaigns.length > 0 && (
            <div className="form-group">
              <label>Campaign (optional):</label>
              <select
                value={offer.selectedCampaign}
                onChange={(e) => setOffer({...offer, selectedCampaign: e.target.value})}
              >
                <option value="">Create new campaign</option>
                {campaigns.filter(c => c.status === 'active').map(campaign => (
                  <option key={campaign.campaignId} value={campaign.campaignId}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Distribution Settings</h2>
          
          <div className="form-group">
            <label>Platforms *:</label>
            <div className="platform-selection">
              {accounts.map(account => (
                <label key={account.accountId} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={offer.platforms.includes(account.platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setOffer({
                          ...offer, 
                          platforms: [...offer.platforms, account.platform]
                        });
                      } else {
                        setOffer({
                          ...offer, 
                          platforms: offer.platforms.filter(p => p !== account.platform)
                        });
                      }
                    }}
                  />
                  <span>
                    {PLATFORM_ICONS[account.platform as keyof typeof PLATFORM_ICONS]} {account.accountName}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Distribution Type:</label>
            <select
              value={offer.distributionType}
              onChange={(e) => setOffer({...offer, distributionType: e.target.value as any})}
            >
              <option value="immediate">Post Immediately</option>
              <option value="scheduled">Schedule for Later</option>
            </select>
          </div>

          {offer.distributionType === 'scheduled' && (
            <div className="form-group">
              <label>Scheduled Date & Time:</label>
              <input
                type="datetime-local"
                value={offer.scheduledDates[0] || ''}
                onChange={(e) => setOffer({
                  ...offer, 
                  scheduledDates: [e.target.value]
                })}
              />
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Discount Code Generation</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={offer.generateCodes}
                onChange={(e) => setOffer({...offer, generateCodes: e.target.checked})}
              />
              Generate unique discount codes for this offer
            </label>
          </div>

          {offer.generateCodes && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Code Prefix:</label>
                  <input
                    type="text"
                    value={offer.codePrefix}
                    onChange={(e) => setOffer({...offer, codePrefix: e.target.value.toUpperCase()})}
                    maxLength={10}
                    placeholder="SOCIAL"
                  />
                </div>

                <div className="form-group">
                  <label>Codes per Platform:</label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={offer.codesPerPlatform}
                    onChange={(e) => setOffer({...offer, codesPerPlatform: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Code Type:</label>
                  <select
                    value={offer.codeType}
                    onChange={(e) => setOffer({
                      ...offer, 
                      codeType: e.target.value as any,
                      maxUsesPerCode: e.target.value === 'single_use' ? 1 : offer.maxUsesPerCode
                    })}
                  >
                    <option value="single_use">Single Use</option>
                    <option value="multi_use">Multi Use</option>
                    <option value="unlimited">Unlimited Use</option>
                  </select>
                </div>

                {offer.codeType !== 'unlimited' && (
                  <div className="form-group">
                    <label>Max Uses per Code:</label>
                    <input
                      type="number"
                      min={1}
                      value={offer.maxUsesPerCode}
                      onChange={(e) => setOffer({...offer, maxUsesPerCode: parseInt(e.target.value) || 1})}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Code Validity (days):</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={offer.codeValidityDays}
                    onChange={(e) => setOffer({...offer, codeValidityDays: parseInt(e.target.value) || 30})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Min Purchase Amount ($):</label>
                  <input
                    type="number"
                    min="0"
                    step={0.01}
                    value={offer.minPurchaseAmount}
                    onChange={(e) => setOffer({...offer, minPurchaseAmount: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div className="form-group">
                  <label>Max Discount Amount ($):</label>
                  <input
                    type="number"
                    min="0"
                    step={0.01}
                    value={offer.maxDiscountAmount}
                    onChange={(e) => setOffer({...offer, maxDiscountAmount: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h2>Advanced Options</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={offer.enableBoosting}
                onChange={(e) => setOffer({...offer, enableBoosting: e.target.checked})}
              />
              Enable post boosting (paid promotion)
            </label>
          </div>

          {offer.enableBoosting && (
            <div className="form-row">
              <div className="form-group">
                <label>Budget per Post ($):</label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={offer.boostBudgetPerPost}
                  onChange={(e) => setOffer({...offer, boostBudgetPerPost: parseFloat(e.target.value) || 25})}
                />
              </div>

              <div className="form-group">
                <label>Boost Duration (days):</label>
                <input
                  type="number"
                  min="1"
                  max={30}
                  value={offer.boostDuration}
                  onChange={(e) => setOffer({...offer, boostDuration: parseInt(e.target.value) || 7})}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={offer.enableTracking}
                onChange={(e) => setOffer({...offer, enableTracking: e.target.checked})}
              />
              Enable UTM tracking and analytics
            </label>
          </div>

          {offer.enableTracking && (
            <div className="form-row">
              <div className="form-group">
                <label>UTM Campaign:</label>
                <input
                  type="text"
                  value={offer.utmCampaign}
                  onChange={(e) => setOffer({...offer, utmCampaign: e.target.value})}
                />
              </div>
            </div>
          )}
        </div>

        {renderPreview()}

        <div className="distribution-actions">
          <button
            onClick={handleDistributeOffer}
            disabled={loading || !offer.content || offer.platforms.length === 0 || !offer.selectedDiscount}
            className="distribute-btn"
          >
            {loading ? 'Distributing...' : 
             `Distribute to ${offer.platforms.length} Platform${offer.platforms.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};